import { useState } from "react";
import { motion } from "framer-motion";
import { Activity, Music2, Waves, FileAudio, Disc3, CircleDot, Sparkles, Timer, Check, AlertTriangle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtTime, padCh } from "./dawHelpers";

/** Common-music key options — 12 major + 12 minor, in chromatic order. */
const KEY_OPTIONS = [
  "C Major", "C# Major", "D Major", "D# Major", "E Major", "F Major",
  "F# Major", "G Major", "G# Major", "A Major", "A# Major", "B Major",
  "A Minor", "A# Minor", "B Minor", "C Minor", "C# Minor", "D Minor",
  "D# Minor", "E Minor", "F Minor", "F# Minor", "G Minor", "G# Minor",
];

/** Base pill — renders as a <button> when onClick is supplied, otherwise a <div>.
 *  Hover/focus affordances only kick in for the button variant so non-interactive
 *  pills don't tease users into clicking them.
 *  iter-104 — `iconAnim` is an optional class applied to the icon-wrapping span
 *  to drive a signature CSS animation on the inline SVG (see index.css). */
function StatPill({ icon, label, value, onClick, testId, active, iconAnim }) {
  const base =
    "studio-pill flex items-center justify-between gap-2.5 rounded-2xl border px-3 py-2.5 text-left transition-colors";
  const iconCls = `${active ? "text-fuchsia-200" : "text-white/55"}${iconAnim ? ` ${iconAnim}` : ""}`;
  if (onClick) {
    return (
      <motion.button
        type="button"
        onClick={onClick}
        data-testid={testId}
        whileHover={{ y: -1 }}
        whileTap={{ y: 0, scale: 0.98 }}
        transition={{ type: "spring", stiffness: 500, damping: 28 }}
        className={`${base} ${active
          ? "border-fuchsia-400/45 bg-fuchsia-500/[0.12] shadow-[0_0_18px_rgba(217,70,239,0.22)]"
          : "border-white/[0.08] bg-white/[0.025] hover:border-white/20 hover:bg-white/[0.05]"
        } cursor-pointer focus:outline-none focus:ring-2 focus:ring-fuchsia-400/40`}
      >
        <div className="flex items-center gap-2">
          <span className={iconCls}>{icon}</span>
          <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${active ? "text-fuchsia-100/85" : "text-white/55"}`}>
            {label}
          </span>
        </div>
        <span className={`font-mono text-[12px] tabular-nums ${active ? "text-fuchsia-50" : "text-white"}`}>{value}</span>
      </motion.button>
    );
  }
  return (
    <div data-testid={testId} className={`${base} border-white/[0.08] bg-white/[0.025]`}>
      <div className="flex items-center gap-2">
        <span className={iconAnim ? `text-white/55 ${iconAnim}` : "text-white/55"}>{icon}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">{label}</span>
      </div>
      <span className="font-mono text-[12px] tabular-nums text-white">{value}</span>
    </div>
  );
}

/** Tempo pill — opens a Popover with a number input (60–240). Persists via the
 *  onTempoChange callback (parent decides localStorage). iter-104: when `playing`
 *  is true, the icon pulses to a fixed 1.2 s tempo as a visual heartbeat. */
function TempoPill({ tempo, onTempoChange, playing = false }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(tempo);

  const commit = (val) => {
    const clamped = Math.max(60, Math.min(240, Math.round(Number(val) || tempo)));
    onTempoChange(clamped);
    setDraft(clamped);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setDraft(tempo); }}>
      <PopoverTrigger asChild>
        <motion.button
          type="button"
          data-testid="stat-pill-tempo"
          whileHover={{ y: -1 }}
          whileTap={{ y: 0, scale: 0.98 }}
          transition={{ type: "spring", stiffness: 500, damping: 28 }}
          className="studio-pill flex items-center justify-between gap-2.5 rounded-2xl border border-white/[0.08] bg-white/[0.025] px-3 py-2.5 text-left transition-colors hover:border-white/20 hover:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-fuchsia-400/40"
          aria-haspopup="dialog"
        >
          <div className="flex items-center gap-2">
            <span className={playing ? "stat-anim-tempo text-fuchsia-200" : "text-white/55"}>
              <Music2 className="h-3.5 w-3.5" />
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">Tempo</span>
          </div>
          <span className="font-mono text-[12px] tabular-nums text-white">{tempo} BPM</span>
        </motion.button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="glass-card w-60 rounded-2xl border-0 text-white"
        data-testid="tempo-popover"
      >
        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">Session tempo</p>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={60}
            max={240}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { commit(draft); setOpen(false); }
              if (e.key === "Escape") { setOpen(false); }
            }}
            data-testid="tempo-input"
            className="w-20 rounded-lg border border-white/10 bg-black/40 px-2 py-1 text-center font-mono text-base tabular-nums text-white focus:border-fuchsia-400/60 focus:outline-none"
            autoFocus
          />
          <span className="font-mono text-[11px] text-white/55">BPM</span>
          <button
            type="button"
            data-testid="tempo-set-btn"
            onClick={() => { commit(draft); setOpen(false); }}
            className="studio-pill ml-auto rounded-lg bg-fuchsia-500 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-white hover:bg-fuchsia-400"
          >
            <Check className="mr-1 inline h-3 w-3" />Set
          </button>
        </div>
        <div className="mt-3 grid grid-cols-4 gap-1">
          {[80, 100, 120, 128, 140, 160, 174, 200].map((preset) => (
            <button
              key={`bpm-${preset}`}
              type="button"
              data-testid={`tempo-preset-${preset}`}
              onClick={() => { commit(preset); setOpen(false); }}
              className={`rounded-md px-1.5 py-1 font-mono text-[10px] transition ${
                tempo === preset
                  ? "bg-fuchsia-500/30 text-fuchsia-100"
                  : "bg-white/[0.04] text-white/70 hover:bg-white/[0.1]"
              }`}
            >
              {preset}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Key pill — opens a Popover with a 24-key picker grid. */
function KeyPill({ musicalKey, onKeyChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <motion.button
          type="button"
          data-testid="stat-pill-key"
          whileHover={{ y: -1 }}
          whileTap={{ y: 0, scale: 0.98 }}
          transition={{ type: "spring", stiffness: 500, damping: 28 }}
          className="studio-pill flex items-center justify-between gap-2.5 rounded-2xl border border-white/[0.08] bg-white/[0.025] px-3 py-2.5 text-left transition-colors hover:border-white/20 hover:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-fuchsia-400/40"
          aria-haspopup="dialog"
        >
          <div className="flex items-center gap-2">
            <Waves className="h-3.5 w-3.5 text-white/55" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">Key</span>
          </div>
          <span className="font-mono text-[12px] tabular-nums text-white">{musicalKey}</span>
        </motion.button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="glass-card w-72 rounded-2xl border-0 text-white"
        data-testid="key-popover"
      >
        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">Session key</p>
        <div className="grid grid-cols-4 gap-1">
          {KEY_OPTIONS.map((k) => (
            <button
              key={`key-${k}`}
              type="button"
              data-testid={`key-option-${k.replace(/[\s#]/g, "-")}`}
              onClick={() => { onKeyChange(k); setOpen(false); }}
              className={`rounded-md px-1.5 py-1 font-mono text-[10px] transition ${
                musicalKey === k
                  ? "bg-fuchsia-500/30 text-fuchsia-100 ring-1 ring-fuchsia-400/50"
                  : "bg-white/[0.04] text-white/75 hover:bg-white/[0.1]"
              }`}
            >
              {k.replace(" Major", "").replace(" Minor", "m")}
            </button>
          ))}
        </div>
        <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.18em] text-white/35">
          Tip: session-only label — no audio is transposed.
        </p>
      </PopoverContent>
    </Popover>
  );
}

export function StatusStrip({
  engineState, playing, loadedCount, maxDur, recordingIdx,
  tempo, musicalKey, dryMode, metronomeOn,
  onEngineClick, onTempoChange, onKeyChange, onLoadedClick, onLengthClick, onRecClick,
  onToggleDryMode, onToggleMetronome,
  clipCount = 0,
  onClipPillClick,
}) {
  const showClipPill = clipCount > 0;
  return (
    <div className={`mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4 ${showClipPill ? "lg:grid-cols-9" : "lg:grid-cols-8"}`}>
      <StatPill
        testId="stat-pill-engine"
        icon={<Activity className="h-3.5 w-3.5" />}
        label="Engine"
        value={playing ? "Live" : engineState === "running" ? "Ready" : "Idle"}
        onClick={onEngineClick}
        active={playing}
        iconAnim={playing ? "stat-anim-engine-live" : undefined}
      />
      <TempoPill tempo={tempo} onTempoChange={onTempoChange} playing={playing} />
      <KeyPill musicalKey={musicalKey} onKeyChange={onKeyChange} />
      <StatPill
        testId="stat-pill-dry"
        icon={<Sparkles className="h-3.5 w-3.5" />}
        label="Mode"
        value={dryMode ? "Dry" : "Wet"}
        onClick={onToggleDryMode}
        active={dryMode}
      />
      <StatPill
        testId="stat-pill-metronome"
        icon={<Timer className="h-3.5 w-3.5" />}
        label="Click"
        value={metronomeOn ? "On" : "Off"}
        onClick={onToggleMetronome}
        active={metronomeOn}
        iconAnim={metronomeOn ? "stat-anim-click-tick" : undefined}
      />
      <StatPill
        testId="stat-pill-loaded"
        icon={<FileAudio className="h-3.5 w-3.5" />}
        label="Loaded"
        value={`${loadedCount} / 64`}
        onClick={onLoadedClick}
      />
      <StatPill
        testId="stat-pill-length"
        icon={<Disc3 className="h-3.5 w-3.5" />}
        label="Length"
        value={fmtTime(maxDur)}
        onClick={onLengthClick}
      />
      <StatPill
        testId="stat-pill-rec"
        icon={<CircleDot className={`h-3.5 w-3.5 ${recordingIdx !== null ? "text-rose-400" : ""}`} />}
        label="Rec"
        value={recordingIdx !== null ? `CH ${padCh(recordingIdx)}` : "—"}
        onClick={onRecClick}
        active={recordingIdx !== null}
        iconAnim={recordingIdx !== null ? "stat-anim-rec-blink" : undefined}
      />
      {/* iter-202 — aggregate clip counter. Slides in as a 9th pill only
          while one or more channels clipped in the last 4 s. Auto-clears
          when transport stops + 1.5 s grace.
          iter-219 — clickable to open the Clip History dropdown. */}
      {showClipPill && (
        <StatPill
          testId="stat-pill-clips"
          icon={<AlertTriangle className="h-3.5 w-3.5 text-red-300" />}
          label="Clips · 4s"
          value={`${clipCount}`}
          active={true}
          iconAnim="stat-anim-rec-blink"
          onClick={onClipPillClick}
        />
      )}
    </div>
  );
}
